import React, { useEffect, useState, useMemo } from 'react';
import { Button, Form, List, Modal } from 'semantic-ui-react';
import { DatePicker } from '../../../../components/shared/DatePicker';
import WeekDaysSelect from '../../../../components/shared/WeekDaysSelect';
import Weekdays from '../../../../components/shared/Weekdays';
import moment from 'moment';
import { TrashButton } from '../../../../components/shared/icons';
import Panel from '../../../../components/shared/Panel';
import NumericInput from '../../../../components/shared/NumericInput';
import { useTextResources, useMessageResources } from '../../../../language';
import PropTypes from 'prop-types';
import { dateFormat, EntityType } from '../../../../components/shared/constants';
import {
  RateCustomerCommissions as RateCustomerCommissionsApi,
  RateAssignment as RateAssignmentApi,
} from '../../../../api/services/next';
import { useConfirm } from '../../../../hooks/useConfirm';
import {
  handleStartEndChange,
  rearangeDateRanges,
} from '../../../../components/shared/utils';
import useCallbackWithLoader from '../../../../hooks/useCallbackWithLoader';

const checkIfNewCommission = (id) =>
  typeof id == 'string' && id.startsWith('newCommission-');

const initialState = {
  commissions: null,
  commissionIdsForDeletion: [],
  startDate: moment().startOf("day").toDate(),
  endDate: moment().add(1, "day").endOf("day").subtract(10, "millisecond").toDate(),
  Weekdays: 0
};

export const CustomerCommissionPopup = ({
  isOpen,
  rateId,
  onClose,
  rateAssignment,
  refreshRateAssignments,
  customerName
}) => {
  const text = useTextResources();
  const message = useMessageResources();

  const [commissions, setCommissions] = useState(initialState.commissions);
  const [commissionIdsForDeletion, setCommissionIdsForDeletion] = useState(initialState.commissionIdsForDeletion);
  const [startDate, setStartDate] = useState(initialState.startDate);
  const [endDate, setEndDate] = useState(initialState.endDate);
  const [Weekdays, setWeekDays] = useState(initialState.Weekdays);
  const callbackWithLoader = useCallbackWithLoader();
  const confirm  = useConfirm();

  useEffect(() => {
    if (!commissions && rateAssignment.Id) {
      RateCustomerCommissionsApi.GetRateCustomerCommissions({
        RateAssignmentId: rateAssignment.Id,
      }).then((res) =>
        setCommissions(res.map(x => ({
          ...x,
          StartDate: new Date(x.StartDate),
          EndDate: new Date(x.EndDate),
        })))
      );
    }
  }, [commissions, isOpen, rateAssignment]);

  const resetState = () => {
    setCommissions(initialState.commissions);
    setCommissionIdsForDeletion(initialState.commissionIdsForDeletion)
    setStartDate(initialState.startDate);
    setEndDate(initialState.endDate);
    setWeekDays(initialState.Weekdays);
  };

  const handleSave = async () => {
    const isSaved = await callbackWithLoader(async () => {
      await deleteCommissionsIfNeeded();
      const rateAssignmentForServer = prepareRateAssignmentForServer();
      await RateAssignmentApi.CreateOrUpdateRateAssignments([
        rateAssignmentForServer,
      ]);
      await refreshRateAssignments();
      return true;
    }, {
      showSuccessMessage: true,
      onErrorResult: false,
    });
    isSaved && handleClosePopup();
  };

  const deleteCommissionsIfNeeded = async () => {
    const commissionsToDeleteFromServer = commissionIdsForDeletion.filter(id => !checkIfNewCommission(id))
    if (commissionsToDeleteFromServer.length) {
      await RateCustomerCommissionsApi.DeleteRateCustomerCommissions(
        commissionsToDeleteFromServer,
      );
    }
  };

  const prepareRateAssignmentForServer = () => {
    const commissionsForServer = commissions
      .filter(c => !commissionIdsForDeletion.includes(c.Id))
      .map(c => ({
        ...c,
        Id: checkIfNewCommission(c.Id) ? 0 : c.Id,
      }));

    return {
      ...rateAssignment,
      RateId: rateId,
      EntityTypeId: EntityType.Customer,
      RateCustomerCommissions: commissionsForServer,
    };
  };

  const handleClosePopup = () => {
    resetState();
    onClose();
  };

  const handleRemoveCommission = async (id) => {
    confirm({
      header: message('AreYouSure'),
      content: message('AreYouSureYouWouldLikeToDeleteSelectedItems'),
      confirmButton: { content: text('Yes'), positive: true },
      cancelButton: { content: text('No'), negative: true },
      onConfirm: async () => {
        setCommissionIdsForDeletion(prevIds => [...prevIds, id]);
        setCommissions(prevCommissions => prevCommissions.filter(c => c.Id !== id));
      },
    });
  };

  const handleEditCommission = (id, commissionsPercent) => {
    const currentCommissionId = commissions.findIndex((c) => c.Id == id);
    const commissionsCopy = [...commissions];
    commissionsCopy[currentCommissionId].CommissionPercent = commissionsPercent;
    setCommissions(commissionsCopy);
  };

  const handleAddCommission = () => {
    const newCommission = {
      Id: `newCommission-${crypto.randomUUID()}`,
      RateAssignmentId: rateAssignment.Id,
      StartDate: startDate,
      EndDate: endDate,
      Weekdays: Weekdays,
      CommissionPercent: 0,
    };

   const options = {
        getRange: e=> ({range: [e.StartDate, e.EndDate], weekdays: e.Weekdays}),
        setRange: (e, range) => {
          e.StartDate = range[0];
          e.EndDate = range[1];
          e.Weekdays = range.weekdays || e.Weekdays;
        },
        deleteElement: e=> { e.IsActive = false; },
        predicate: (e) => (e.IsActive != false && e.StartDate && e.EndDate),
        copyElement: (e, startDate, endDate) => ({
          ...e,
          Id: 0,
          StartDate: startDate,
          EndDate: endDate,
          Weekdays: e.Weekdays || 0
        })
    }
    const updatedComissions = rearangeDateRanges(newCommission, commissions, options)

    setCommissionIdsForDeletion(prev => [...prev, ...updatedComissions.filter(x => x.IsActive === false).map(x => x.Id)]);
    setCommissions(updatedComissions.filter(item => item.IsActive !== false));

    setStartDate(initialState.startDate);
    setEndDate(initialState.endDate);
  };

  const memoDatesForWeekComponent = useMemo(
    () => (startDate && endDate ? [{ startDate, endDate }] : []),
    [startDate, endDate]
  );

  return (
    <Modal
      size='large'
      open={isOpen}
      onClose={handleClosePopup}
      dimmer
      centered
    >
      <Modal.Header>{`${text('CommissionsForCustomer')} - ${customerName || rateAssignment.CustomerName
        }`}</Modal.Header>
      <Modal.Content
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '60vh',
        }}
      >
        <Form
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Form.Group widths={4} className="date-range-adding">
            <DatePicker label={text("From")} onChange={(value) => handleStartEndChange([value, endDate], 0, setStartDate, setEndDate)} clearable={false} value={startDate} />
            <DatePicker label={text("To")} onChange={(value) => handleStartEndChange([startDate, value], 1, setStartDate, setEndDate)} clearable={false} value={endDate} />
            <WeekDaysSelect onChange={setWeekDays} dates={memoDatesForWeekComponent} />
            <div className="flex-center-center">
              <Button primary onClick={handleAddCommission}>{text("Add")}</Button>
            </div>
          </Form.Group>
          <List className='commissions-list' style={{ overflowY: 'auto' }}>
            {commissions && commissions.length > 0
              ? commissions.map((commission) => (
                <CommissionItem
                  commission={commission}
                  removeCommissionById={() =>
                    handleRemoveCommission(commission.Id)
                  }
                  editCommissionById={handleEditCommission}
                  key={commission.Id}
                />
              ))
              : null}
          </List>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button negative onClick={handleClosePopup}>
          {text('Close')}
        </Button>
        <Button positive onClick={handleSave}>
          {text('Save')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

const CommissionItem = ({
  commission,
  removeCommissionById,
  editCommissionById,
}) => {
  const text = useTextResources();

  return (
    <List.Item key={commission.Id}>
      <Panel
        color={checkIfNewCommission(commission?.Id) ? "red" : ""}
        header={
          <List.Description
            style={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <div>
              {`${moment(commission.StartDate).format(dateFormat)} - ${moment(commission.EndDate).format(
                dateFormat
              )}`}
              <span style={{ marginLeft: 5 }}><Weekdays selectedWeekdaysInt={commission.Weekdays} /></span>
            </div>
            <TrashButton onClick={removeCommissionById} />
          </List.Description>
        }
      >
        <div style={{ display: 'flex' }}>
          <Form.Field
            value={commission.CommissionPercent}
            control={NumericInput}
            min={1}
            label={text('Commission')}
            onChange={(val) => editCommissionById(commission.Id, val)}
            buttonPlacement='right'
          />
        </div>
      </Panel>
    </List.Item>
  );
};

CommissionItem.propTypes = {
  commission: PropTypes.object,
  removeCommissionById: PropTypes.func,
  editCommissionById: PropTypes.func,
};

CustomerCommissionPopup.propTypes = {
  isOpen: PropTypes.bool,
  rateId: PropTypes.number,
  onClose: PropTypes.func,
  refreshRateAssignments: PropTypes.func,
  rateAssignment: PropTypes.object,
  customerName: PropTypes.string
};
